import { Cookies } from 'react-cookie';

import { axios } from './axios';
import createError from '../helpers/create-error';
import { endpoints, ERROR_MESSAGE } from '../constants';
import exportReport from './export-report';
import datastoreUpload from './datastore-upload';
import billingInfo from './billing-info';
import subscription from './subscription';
import onboardingBilling from './onboarding-billing';

const catchAxiosError = error => {
  if (axios.isAxiosError(error)) {
    // Server responded with error status (4xx, 5xx)
    if (error.response) {
      const { data } = error.response;
      throw createError({
        code: data.code,
        message: data.message ?? data.errors[0].message,
      });
    }
    // else if (error.request) {
    //   throw createError({
    //     code: 500,
    //     message: 'No response from server',
    //   });
    // } else {
    //   throw createError({
    //     message: 'Failed to make request',
    //   });
    // }
  }

  // throw createError({
  //   message: 'An unexpected error occurred',
  // });
};

const API = {
  serverUrl: axios.defaults.baseURL,
  get accessToken() {
    return new Cookies().get('accessToken');
  },
  authentication: {
    async create({ email = '', password = '', strategy, token, id = '' }) {
      const { authenticationUrl } = endpoints();

      try {
        const { data } = await axios.post(authenticationUrl, {
          email,
          password,
          strategy,
          token,
          id,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw error;
      }
    },

    async jwt() {
      const { authenticationUrl } = endpoints();
      try {
        const { data } = await axios.post(authenticationUrl, {
          strategy: 'jwt',
          accessToken: API.accessToken,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch user');
      }
    },
  },

  user: {
    async create(payload) {
      const { userUrl } = endpoints();
      try {
        const { data } = await axios.post(userUrl, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw createError({
          code: 502,
          message: 'Something went wrong',
        });
      }
    },
    async get(id = '', params = {}) {
      const { userUrl } = endpoints();
      try {
        const { data } = await axios.get(`${userUrl}/${id}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw error;
      }
    },
    async find(id = '', params = {}) {
      const { userUrl } = endpoints();
      try {
        const { data } = await axios.find(userUrl, id);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch user');
      }
    },
    async update(id = '', payload) {
      const { userUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${userUrl}/${id}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        const message = error?.response?.data?.errors[0]?.message;
        throw new Error(message ?? 'Failed to fetch user');
      }
    },
    async delete(payload) {
      const { deleteUserUrl } = endpoints();
      try {
        const { data } = await axios.post(deleteUserUrl, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to delete user');
      }
    },
  },

  team: {
    async find(params = {}) {
      const { teamUrl } = endpoints();

      try {
        const { data } = await axios.get(teamUrl, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch team members');
      }
    },
    async create(payload) {
      const { teamUrl } = endpoints();

      try {
        const { data } = await axios.post(teamUrl, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to create team member');
      }
    },
    async delete(id, params) {
      const { teamUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${teamUrl}/${id}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to delete team member');
      }
    },
    async sendInvite(payload) {
      const { teamUrl } = endpoints();

      try {
        const { data } = await axios.post(`${teamUrl}/resend-invite`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to resend invite');
      }
    },
  },

  permission: {
    async find(params = {}) {
      const { permissionUrl } = endpoints();

      try {
        const { data } = await axios.get(`${permissionUrl}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          error?.response?.data?.message ?? 'Failed to fetch permissions'
        );
      }
    },
    async update(id, payload) {
      const { permissionUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${permissionUrl}/${id}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          error?.response?.data?.errors?.[0]?.message ??
            'Failed to update permissions'
        );
      }
    },
  },

  business: {
    async find(params = {}) {
      const { businessUrl } = endpoints();

      try {
        const { data } = await axios.get(`${businessUrl}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw error;
      }
    },
    async get(id = '', params = {}) {
      const { businessUrl } = endpoints();

      try {
        const { data } = await axios.get(`${businessUrl}/${id}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw error;
      }
    },
    async create(payload) {
      const { businessUrl } = endpoints();

      try {
        const { data } = await axios.post(`${businessUrl}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to create business');
      }
    },
    async update(id, payload) {
      const { businessUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${businessUrl}/${id}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to update business');
      }
    },
  },

  access: {
    async get(params = {}) {
      const { accessUrl } = endpoints();

      try {
        const { data } = await axios.get(`${accessUrl}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch access');
      }
    },
    async update(id, payload, params) {
      const { accessUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${accessUrl}/${id}`, payload, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to update access');
      }
    },
    async delete(id, params) {
      const { accessUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${accessUrl}/${id}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to delete access');
      }
    },
  },

  billing: {
    async getInfo(businessId) {
      const { billingUrl } = endpoints();

      try {
        const { data } = await axios.get(`${billingUrl}`, {
          params: { businessId },
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch billing');
      }
    },
    async getCheckoutPage(payload) {
      const { billingUrl } = endpoints();

      try {
        const { data } = await axios.post(`${billingUrl}`, {
          action: 'checkout',
          ...payload,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to access checkout');
      }
    },
    async getPortalPage(payload) {
      const { billingUrl } = endpoints();

      try {
        const { data } = await axios.post(`${billingUrl}`, {
          action: 'portal',
          ...payload,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to access portal page');
      }
    },
  },

  clients: {
    async find(params = {}) {
      const { clientUrl } = endpoints();

      try {
        const { data } = await axios.get(`${clientUrl}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(error?.response?.data?.message);
      }
    },
    async get(id = '', params = {}) {
      const { clientUrl } = endpoints();

      try {
        const { data } = await axios.get(`${clientUrl}/${id}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(error?.response?.data?.message);
      }
    },
    async create(payload) {
      const { clientUrl } = endpoints();

      try {
        const { data } = await axios.post(`${clientUrl}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(error?.response?.data?.message);
      }
    },
    async update(id, payload) {
      const { clientUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${clientUrl}/${id}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to update client');
      }
    },
    async delete(id, params) {
      const { clientUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${clientUrl}/${id}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to delete client');
      }
    },
  },

  accounts: {
    async find(params = {}, signal) {
      const { accountsUrl } = endpoints();

      try {
        const { data } = await axios.get(`${accountsUrl}`, {
          params,
          signal,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch accounts');
      }
    },
    async get(id = '', params = {}) {
      const { accountsUrl } = endpoints();

      try {
        const { data } = await axios.get(`${accountsUrl}/${id}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch account');
      }
    },
    async update(id, payload, params = {}) {
      const { accountsUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${accountsUrl}/${id}`, payload, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to update account');
      }
    },

    async delete(id = '', params) {
      const { accountsUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${accountsUrl}/${id}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to delete account');
      }
    },
  },

  subAccounts: {
    async update(id, payload, params) {
      const { subAccountsUrl } = endpoints();
      try {
        const { data } = await axios.patch(`${subAccountsUrl}/${id}`, payload, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to update sub account');
      }
    },
  },

  reports: {
    async find(params = {}) {
      const { reportsUrl } = endpoints();

      try {
        const { data } = await axios.get(`${reportsUrl}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch reports');
      }
    },
    async get(id = '', params = {}) {
      const { reportsUrl } = endpoints();

      try {
        const { data } = await axios.get(`${reportsUrl}/${id}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch report');
      }
    },
    async create(payload) {
      const { reportsUrl } = endpoints();

      try {
        const { data } = await axios.post(`${reportsUrl}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(error?.response?.data?.errors?.[0]?.message);
      }
    },

    async update(id = '', payload = {}) {
      const { reportsUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${reportsUrl}/${id}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to update report');
      }
    },

    async delete(id = '', params = {}) {
      const { reportsUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${reportsUrl}/${id}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to delete report');
      }
    },

    async publish(id = '', params = {}) {
      const { publishReportsUrls } = endpoints();

      try {
        const { data } = await axios.post(
          publishReportsUrls,
          { id },
          { params }
        );
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to publish report');
      }
    },
  },

  templates: {
    async get(params = {}) {
      const { templatesUrl } = endpoints();

      try {
        const { data } = await axios.get(`${templatesUrl}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          error?.response?.data?.message ?? 'Failed to fetch templates'
        );
      }
    },
  },

  globalTemplates: {
    async get(params = {}) {
      const { globalTemplatesUrl } = endpoints();

      try {
        const { data } = await axios.get(`${globalTemplatesUrl}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(
          error?.response?.data?.message ?? 'Failed to fetch templates'
        );
      }
    },
  },

  authManagement: {
    async sendResetPasswordEmail(email) {
      const { authManagementUrl } = endpoints();

      try {
        const { data } = await axios.post(`${authManagementUrl}`, {
          action: 'sendResetPwd',
          value: { email },
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to reset password');
      }
    },
    async updatePassword(value) {
      const { authManagementUrl } = endpoints();

      try {
        const { data } = await axios.post(`${authManagementUrl}`, {
          action: 'resetPwdLong',
          value,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          error?.response?.data?.message ?? 'Failed to reset password'
        );
      }
    },
    async changePassword(value) {
      const { authManagementUrl } = endpoints();

      try {
        const { data } = await axios.post(`${authManagementUrl}`, {
          action: 'passwordChange',
          value,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          error?.response?.data?.message ?? 'Failed to change password'
        );
      }
    },
    async verifySignup(token) {
      const { authManagementUrl } = endpoints();

      try {
        const { data } = await axios.post(`${authManagementUrl}`, {
          action: 'passwordChange',
          value: token,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          error?.response?.data?.message ?? 'Failed to verify signup'
        );
      }
    },
  },

  insights: {
    async get(url = '') {
      const { insightsUrl } = endpoints();

      try {
        const { data } = await axios.get(`${insightsUrl}/?${url}`);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch insights');
      }
    },
  },

  ads: {
    async get(params) {
      const { adsUrl } = endpoints();

      try {
        const { data } = await axios.get(adsUrl, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch ads');
      }
    },
  },

  website: {
    async get(params) {
      const { websiteUrl } = endpoints();

      try {
        const { data } = await axios.get(websiteUrl, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch website data');
      }
    },
  },

  mail: {
    async get(params) {
      const { mailUrl } = endpoints();

      try {
        const { data } = await axios.get(mailUrl, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to fetch mail data');
      }
    },
  },

  tasklist: {
    async find(params = {}) {
      const { tasklistUrl } = endpoints();

      try {
        const { data } = await axios.get(`${tasklistUrl}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch tasklist');
      }
    },
    async create(payload) {
      const { tasklistUrl } = endpoints();

      try {
        const { data } = await axios.post(`${tasklistUrl}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to create tasklist');
      }
    },
  },

  task: {
    async find(params = {}) {
      const { taskUrl } = endpoints();

      try {
        const { data } = await axios.get(`${taskUrl}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch tasks');
      }
    },
    async create(params = {}, payload) {
      const { taskUrl } = endpoints();

      try {
        const { data } = await axios.post(`${taskUrl}`, payload, params);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to create task');
      }
    },
    async patch(id = '', params = {}, payload) {
      const { taskUrl } = endpoints();

      try {
        const { data } = await axios.patch(`${taskUrl}/${id}`, payload, params);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to patch task');
      }
    },
    async delete(id = '', params = {}) {
      const { taskUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${taskUrl}/${id}`, params);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to delete task');
      }
    },
  },
  demo: {
    async get(params) {
      const { demoUrl } = endpoints();

      try {
        const { data } = await axios.get(`${demoUrl}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch demo client');
      }
    },
  },
  competitors: {
    async search(params = {}) {
      const { competitorSearchUrl } = endpoints();

      try {
        const { data } = await axios.get(`${competitorSearchUrl}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          error?.response?.data?.message ?? 'Failed to fetch competitors'
        );
      }
    },
    async create(competitor) {
      const { competitorUrl } = endpoints();

      try {
        const { data } = await axios.post(`${competitorUrl}`, competitor);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to create competitors');
      }
    },
    async find(params = {}) {
      const { competitorUrl } = endpoints();

      try {
        const { data } = await axios.get(`${competitorUrl}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch competitors');
      }
    },
    async delete(id) {
      const { competitorUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${competitorUrl}/${id}`);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to delete competitor');
      }
    },
  },
  publicInsights: {
    async get(params) {
      const { publicInsightsUrl } = endpoints();

      try {
        const { data } = await axios.get(`${publicInsightsUrl}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to fetch public insights');
      }
    },
  },
  upvote: {
    async getBusiness(params) {
      const { upvoteUrl } = endpoints();

      try {
        const { data } = await axios.get(`${upvoteUrl}`, { params });
        return data;
      } catch (error) {
        if (error.message === ERROR_MESSAGE.status) {
          // throw new Error(ERROR_MESSAGE.msg);
        }
        // throw new Error('Failed to fetch upvotes.');
      }
      return [];
    },
    async getSummury(params) {
      const { upvoteSummaryUrl } = endpoints();

      try {
        const { data } = await axios.get(`${upvoteSummaryUrl}`, { params });
        return data;
      } catch (error) {
        if (error.message === ERROR_MESSAGE.status) {
          // throw new Error(ERROR_MESSAGE.msg);
        }
        // throw new Error('Failed to fetch upvotes.');
      }
      return [];
    },
    async post(payload) {
      const { upvoteUrl } = endpoints();

      try {
        const { data } = await axios.post(`${upvoteUrl}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to post upvotes.');
      }
    },
    async delete(id, params) {
      const { upvoteUrl } = endpoints();

      try {
        const { data } = await axios.delete(`${upvoteUrl}/${id}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error('Failed to unvote.');
      }
    },
  },

  datasource: {
    async post(businessId, payload) {
      const { datasourceUrl } = endpoints();
      try {
        const { data } = await axios.post(
          `${datasourceUrl + businessId}`,
          payload
        );
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? '',
          })
        );
      }
    },
  },
  exportReport,
  datastoreUpload,
  billingInfo,
  reportsResync: {
    async create(payload, params) {
      const { reportsResyncUrl } = endpoints();

      try {
        const { data } = await axios.post(`${reportsResyncUrl}`, payload, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(error?.response?.data?.errors?.[0]?.message);
      }
    },
  },
  invitedUser: {
    async create(payload) {
      const { invitedUserUrl } = endpoints();
      try {
        const { data } = await axios.post(`${invitedUserUrl}`, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? '',
          })
        );
      }
    },
  },
  subscription,
  coupon: {
    async find(params) {
      const { couponUrl } = endpoints();
      try {
        const { data } = await axios.get(`${couponUrl}`, { params });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? '',
          })
        );
      }
    },
  },
  onboardingBilling,
  customMetrics: {
    async find() {
      const { metrics } = endpoints();
      try {
        const { data } = await axios.get(metrics);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? '',
          })
        );
      }
    },
  },
  datasourceMetrics: {
    async get() {
      const { datasourceMetrics } = endpoints();
      try {
        const { data } = await axios.get(datasourceMetrics);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? '',
          })
        );
      }
    },
    async post(payload) {
      const { datasourceMetrics } = endpoints();
      try {
        const { data } = await axios.post(datasourceMetrics, payload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error('Failed to insert metrics');
      }
    },
  },
  paddleSubscription: {
    async get(id = '', params = '') {
      const { paddleSubscriptionUrl } = endpoints();
      try {
        const { data } = await axios.get(`${paddleSubscriptionUrl}/${id}`, {
          params,
        });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? 'Failed to fetch Plan',
          })
        );
      }
    },
    async update(id = '', payload = {}) {
      const { paddleSubscriptionUrl } = endpoints();
      try {
        const { data } = await axios.patch(
          `${paddleSubscriptionUrl}/${id}`,
          payload
        );
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message:
              error?.response?.data?.message ?? 'Failed to update payment Plan',
          })
        );
      }
    },
  },
  destinations: {
    async get(signal) {
      const { destinationProviders } = endpoints();
      try {
        const { data } = await axios.get(destinationProviders, { signal });
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message:
              error?.response?.data?.message ?? 'Failed to fetch destinations',
          })
        );
      }
    },
    async delete(id) {
      const { destinations: destinationUrl } = endpoints();
      try {
        const { data } = await axios.delete(`${destinationUrl}/${id}`);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message:
              error?.response?.data?.message ??
              'Failed to disconnect destination',
          })
        );
      }
    },
  },
  charts: {
    async find(params) {
      const { charts: chartsUrl } = endpoints();
      try {
        const response = await axios.get(`${chartsUrl}?${params}`);
        return response.data;
      } catch (error) {
        catchAxiosError(error);
        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? 'Failed to fetch charts',
          })
        );
      }
    },
    async findById(id) {
      const { charts: chartsUrl } = endpoints();
      try {
        const response = await axios.get(`${chartsUrl}/${id}`);
        return response.data;
      } catch (error) {
        catchAxiosError(error);
        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }
        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? 'Failed to fetch chart',
          })
        );
      }
    },
    async create(businessId, payload) {
      const { charts: chartsUrl } = endpoints();
      try {
        const response = await axios.post(
          `${chartsUrl}?businessId=${businessId}`,
          payload
        );
        return response.data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? 'Failed to create chart',
          })
        );
      }
    },
    async delete(id) {
      const { charts: chartsUrl } = endpoints();
      try {
        const { data } = await axios.delete(`${chartsUrl}/${id}`);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? 'Failed to delete chart',
          })
        );
      }
    },
    async update(id = '', payload = {}) {
      const { charts: chartsUrl } = endpoints();
      try {
        // patch don't accept "type" in body
        const { type, ...patchPayload } = payload;
        const { data } = await axios.patch(`${chartsUrl}/${id}`, patchPayload);
        return data;
      } catch (error) {
        catchAxiosError(error);

        if (error.message === ERROR_MESSAGE.status) {
          throw new Error(ERROR_MESSAGE.msg);
        }

        throw new Error(
          JSON.stringify({
            code: error?.message ?? '',
            message: error?.response?.data?.message ?? 'Failed to update chart',
          })
        );
      }
    },
  },
};

export default API;
